import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import {
  ApiBaseRequest,
  ApiResponse,
  SurveyLogicRulesConfig,
  Flow,
  FlowTemplate,
  HttpBaseRequest,
  QuestionStyles,
  SaveFlowTemplateRequest,
  SimpleFlow,
  SimpleFlowQuestion,
  SimpleFlowQuestionOption,
} from '@vision/ui/interfaces';
import { buildEndpointWithQuery, createHttpExtraOptions, insertIfObject } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';

export const FlowsService = createApi({
  reducerPath: 'FlowsService',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getFlows: builder.query<
      Flow[],
      HttpBaseRequest<{
        nodeId: string;
        includeQuestionOptions?: boolean;
        includeQuestions?: boolean;
        allowSavingIncompleteSurvey?: boolean;
      }>
    >({
      query: ({ nodeId, includeQuestionOptions, includeQuestions, allowSavingIncompleteSurvey }) => ({
        url: '/v1/flows',
        method: HttpMethod.GET,
        params: {
          node_id: nodeId,
          include_questions: includeQuestions,
          include_options: includeQuestionOptions,
          allow_saving_incomplete_survey: allowSavingIncompleteSurvey,
        },
      }),
    }),
    getFlow: builder.query<Flow, { flowId: string; nodeId?: string }>({
      query: ({ flowId, nodeId }) => ({
        url: `/v1/flows/${flowId}`,
        method: HttpMethod.GET,
        params: {
          ...(nodeId && { node_id: nodeId }),
        },
      }),
    }),
    updateFlowConditionRules: builder.mutation<
      { id: string; condition_rules: SurveyLogicRulesConfig[] },
      { accountId: string; flowId: string; conditionRules: SurveyLogicRulesConfig[] }
    >({
      query: ({ accountId, flowId, conditionRules }) => ({
        url: `/v2/accounts/${accountId}/flows/${flowId}/condition_rules`,
        method: HttpMethod.POST,
        data: {
          condition_rules: conditionRules,
        },
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: true,
          },
        }),
      }),
    }),
    saveFlowTemplate: builder.mutation<FlowTemplate, HttpBaseRequest<SaveFlowTemplateRequest>>({
      query: (data) => ({
        url: '/v1/flow_templates',
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
          },
        }),
      }),
    }),
    updateFlowTemplate: builder.mutation<FlowTemplate, HttpBaseRequest<FlowTemplate>>({
      query: (data) => ({
        url: `/v1/flow_templates/${data.id}/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
          },
        }),
      }),
    }),
    fetchFlowsWithPagination: builder.query<
      ApiResponse<SimpleFlow[]>,
      ApiBaseRequest & {
        includeQuestions?: boolean;
        questionStyles?: QuestionStyles[];
        questionCategoryNotNull?: boolean;
        questionKeyNotNull?: boolean;
      }
    >({
      query: ({
        nodeId,
        query,
        sortBy,
        sortOrder,
        includeQuestions,
        questionStyles,
        questionCategoryNotNull,
        questionKeyNotNull,
      }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/flows`, query),
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!includeQuestions, {
            include_questions: includeQuestions,
          }),
          ...insertIfObject(!!questionStyles && !!questionStyles.length, {
            question_styles: questionStyles?.join(','),
          }),
          ...insertIfObject(!!questionCategoryNotNull, {
            question_category_not_null: questionCategoryNotNull,
          }),
          ...insertIfObject(!!questionKeyNotNull, {
            question_key_not_null: questionKeyNotNull,
          }),
          ...insertIfObject(!!sortBy, {
            sort_by: sortBy,
          }),
          ...insertIfObject(!!sortOrder, {
            sort_order: sortOrder,
          }),
        },
      }),
    }),
    fetchFlowQuestionsWithPagination: builder.query<
      ApiResponse<SimpleFlowQuestion[]>,
      ApiBaseRequest & {
        flowIds?: string[];
        style?: QuestionStyles[];
        keyNotNull?: boolean;
        categoryNotNull?: boolean;
      }
    >({
      query: ({ nodeId, flowIds, style, query, keyNotNull, categoryNotNull, sortBy, sortOrder }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/flows/questions`, query),
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!flowIds && !!flowIds.length, {
            flow_ids: flowIds?.join(','),
          }),
          ...insertIfObject(!!style && !!style.length, {
            style: style?.join(','),
          }),
          ...insertIfObject(!!keyNotNull, {
            key_not_null: keyNotNull,
          }),
          ...insertIfObject(!!categoryNotNull, {
            category_not_null: categoryNotNull,
          }),
          ...insertIfObject(!!sortBy, {
            sort_by: sortBy,
          }),
          ...insertIfObject(!!sortOrder, {
            sort_order: sortOrder,
          }),
        },
      }),
    }),
    fetchFlowQuestionOptionsWithPagination: builder.query<
      ApiResponse<SimpleFlowQuestionOption[]>,
      ApiBaseRequest & { questionIds?: string[]; flowIds?: string[] }
    >({
      query: ({ nodeId, questionIds, query, flowIds, sortBy, sortOrder }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/flows/options`, query),
        method: HttpMethod.GET,
        params: {
          ...insertIfObject(!!questionIds && !!questionIds.length, {
            question_ids: questionIds?.join(','),
          }),
          ...insertIfObject(!!flowIds && !!flowIds.length, {
            flow_ids: flowIds?.join(','),
          }),
          ...insertIfObject(!!sortBy, {
            sort_by: sortBy,
          }),
          ...insertIfObject(!!sortOrder, {
            sort_order: sortOrder,
          }),
        },
      }),
    }),
  }),
});

export const {
  useGetFlowsQuery,
  useLazyGetFlowsQuery,
  useGetFlowQuery,
  useLazyGetFlowQuery,
  useUpdateFlowConditionRulesMutation,
  useSaveFlowTemplateMutation,
  useUpdateFlowTemplateMutation,
  useLazyFetchFlowsWithPaginationQuery,
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowQuestionOptionsWithPaginationQuery,
} = FlowsService;
